import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Fab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import CidFormModal from '../cidFormModal';
import { useCidCatalog } from '../../../hooks/useCidCatalog';
import { useDeleteCid } from '../hooks/useDeleteCid';

function CidListMobile() {
  const navigate = useNavigate();

  const { cids, isLoading, error: listError, refetch: getCids } = useCidCatalog();
  const { remove } = useDeleteCid();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedCid, setSelectedCid] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCidId, setSelectedCidId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, cidId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCidId(cidId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedCid(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const cid = cids.find((c) => c.id === selectedCidId);
    if (cid) {
      setSelectedCid(cid);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCid(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedCidId(null);
  };

  const handleCidSaved = () => {
    getCids();
    setSuccessMessage('CID salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedCidId)
      .then((res) => {
        getCids();
        setSuccessMessage(res.data ?? 'CID excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir CID.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedCidId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
          <CardHeader
            title="CID"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'text.secondary' }}
          />
          <CardContent>
            <List>
              {cids.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum CID cadastrado
                </Typography>
              ) : (
                cids.map((cid) => (
                  <React.Fragment key={cid.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, cid.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={cid.code}
                        secondary={cid.description}
                        primaryTypographyProps={{ fontWeight: '600' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Fab
        color="primary"
        aria-label="adicionar CID"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={openCreateModal}
      >
        <AddIcon />
      </Fab>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={openEditModal}>Editar</MenuItem>
        <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <CidFormModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleCidSaved}
        cid={selectedCid}
      />

      <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja excluir este CID? Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmModal}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CidListMobile;
